import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { BrowserView, isBrowser, MobileView } from "react-device-detect";
import { Box, TextField, Button, Typography, useTheme, Divider, CircularProgress } from "@mui/material";
import { CheckCircleOutlineOutlined, HighlightOff } from "@mui/icons-material";
import { useUser } from "../hook/useUser";
import { toLocal } from "../utils/date";
import { localLogin, requestResetPassword, resetPassword, validateRequest } from "../api/user";
import { authorize, getToken, ssoLogin } from "../api/sso";
import dayjs from "dayjs";
import PasswordField from "../component/PasswordField";

const LoginPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [error, setError] = useState("");
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [showResetSuccess, setShowResetSuccess] = useState(false);
  const [ssoStatus, setSsoStatus] = useState(null);
  const [failedAttempts, setFailedAttempts] = useState(Number.parseInt(localStorage.getItem("loginAttempt")) || 0);
  const [accountLockedUntil, setAccountLockedUntil] = useState(null);
  const [email, setEmail] = useState("");
  const [isValidRequest, setIsValidRequest] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const requestId = searchParams.get("requestId");
  const privileged = searchParams.get("privileged");
  const authorizationCode = searchParams.get("code");
  const reset = searchParams.get("reset");
  const [token, setToken] = useState(localStorage.getItem("token"));
  const { user, isGettingUser } = useUser(undefined, token);
  const lockTime = 15 * 60 * 1000;

  useEffect(() => {
    if (user && user !== "Token Expired") {
      if (user.userRole === "System Administrator" && isBrowser) {
        navigate("/modules/SystemAdmin");
      } else {
        navigate("/modules/Portfolio");
      }
    }
  }, [user, navigate]);

  useEffect(() => {
    if (isGettingUser) {
      setSsoStatus("Getting user information...");
    } else {
      setSsoStatus();
    }
  }, [isGettingUser]);

  useEffect(() => {
    if (requestId) {
      validateRequestId();
    }
  }, [requestId]);

  useEffect(() => {
    if (authorizationCode) {
      console.log("authorizationCode", authorizationCode);
      getSSOUser();
    }
  }, [authorizationCode]);

  const getSSOUser = async () => {
    setSsoStatus("Login in using your organization account...");
    try {
      const data = await getToken(authorizationCode);
      console.log("token", data);
      if (data) {
        const ssoData = await ssoLogin(data.access_token);
        console.log("ssoData", ssoData);
        localStorage.setItem("token", ssoData.access_token);
        localStorage.setItem("exp", new Date().getTime() + 30 * 60 * 1000);
        localStorage.setItem("refreshToken", ssoData.refresh_token);
        localStorage.setItem("loginAttempt", 0);
        setToken(ssoData.access_token);
        setError("");
        setFailedAttempts(0);
        setSsoStatus(null);
      } else {
        setSsoStatus("failed");
      }
    } catch (error) {
      setSsoStatus("failed");
    }
  };

  const validateRequestId = async () => {
    try {
      const response = await validateRequest(requestId);
      if (response.data) {
        setIsValidRequest(true);
      }
    } catch {
      setIsValidRequest(false);
    }
  };

  const login = useCallback(async () => {
    if (!username || !password) {
      setError("Username and password are required.");
      return;
    }
    // reset login attempt after 15 mins
    if (localStorage.getItem("lastLoginAttempt")) {
      if (new Date().getTime() - new Date(localStorage.getItem("lastLoginAttempt")).getTime() > lockTime) {
        localStorage.setItem("loginAttempt", 0);
      }
    }
    if (localStorage.getItem("loginAttempt") >= 5) {
      setAccountLockedUntil(new Date(new Date(localStorage.getItem("lastLoginAttempt")).getTime() + lockTime));
    } else {
      localStorage.setItem("lastLoginAttempt", new Date().toISOString());
      try {
        setSsoStatus("Login in using your Project Workplace account...");
        const data = await localLogin(username, password);
        localStorage.setItem("token", data.access_token);
        localStorage.setItem("exp", new Date().getTime() + 30 * 60 * 1000);
        localStorage.setItem("refreshToken", data.refresh_token);
        localStorage.setItem("loginAttempt", 0);
        setToken(data.access_token);
        setError("");
        setFailedAttempts(0);
        setSsoStatus();
      } catch (error) {
        if (error.response.data.detail.loginAttempts) {
          setError("Invalid email address or password");
          setFailedAttempts(error.response.data.detail.loginAttempts);
        } else if (error.response.data.detail.accountLockedFrom) {
          setAccountLockedUntil(toLocal(error.response.data.detail.accountLockedFrom).add(15, "minute"));
        } else {
          setError(error.response.data.detail);
          setFailedAttempts(failedAttempts + 1);
          localStorage.setItem("loginAttempt", failedAttempts + 1);
        }
        setSsoStatus();
      }
    }
  }, [username, password, failedAttempts]);

  const handleSingleSignOn = () => {
    window.location.href = authorize();
  };

  const handleClickForgotPassword = () => {
    setError("");
    setShowResetPassword(true);
    setShowResetSuccess(false);
    setSsoStatus(null);
    setAccountLockedUntil(null);
  };

  const validatePassword = (password) => {
    let errorMessage = "";

    // privileged account (Executive User, Account System admin and project admin)
    if (privileged) {
      if (password.length < 20) {
        errorMessage =
          "Password must be at least 20 characters and contain at least one Uppercase character, one Lowercase character, one digit, one special character and not contain any consecutive or identical characters.";
      } else {
        const uppercaseRegex = /[A-Z]/;
        const lowercaseRegex = /[a-z]/;
        const digitRegex = /\d/;
        const specialCharRegex = /[!@#$%^&*(),.?":{}|<>]/;

        if (!uppercaseRegex.test(password)) {
          errorMessage =
            "Password must be at least 20 characters and contain at least one Uppercase character, one Lowercase character, one digit, one special character and not contain any consecutive or identical characters.";
        } else if (!lowercaseRegex.test(password)) {
          errorMessage =
            "Password must be at least 20 characters and contain at least one Uppercase character, one Lowercase character, one digit, one special character and not contain any consecutive or identical characters.";
        } else if (!digitRegex.test(password)) {
          errorMessage =
            "Password must be at least 20 characters and contain at least one Uppercase character, one Lowercase character, one digit, one special character and not contain any consecutive or identical characters.";
        } else if (!specialCharRegex.test(password)) {
          errorMessage =
            "Password must be at least 20 characters and contain at least one Uppercase character, one Lowercase character, one digit, one special character and not contain any consecutive or identical characters.";
        } else {
          const consecutiveCharsRegex = /(\w)\1{1,}/;
          if (consecutiveCharsRegex.test(password)) {
            errorMessage =
              "Password must be at least 20 characters and contain at least one Uppercase character, one Lowercase character, one digit, one special character and not contain any consecutive or identical characters.";
          }
        }
      }
    } else {
      if (password.length < 14) {
        errorMessage =
          "Password must be at least 14 characters and at least 3 of the following character types: One uppercase letter, lowercase letter, number, non-alphanumeric character. Password must not contain any consecutive or identical characters.";
      } else {
        const uppercaseRegex = /[A-Z]/;
        const lowercaseRegex = /[a-z]/;
        const digitRegex = /\d/;
        const specialCharRegex = /[!@#$%^&*(),.?":{}|<>]/;
        let testNotPassed = 0;

        // at least 3 of the following character types: One uppercase letter, lowercase letter, number, non-alphanumeric character
        if (!uppercaseRegex.test(password)) {
          testNotPassed += 1;
        } else if (!lowercaseRegex.test(password)) {
          testNotPassed += 1;
        } else if (!digitRegex.test(password)) {
          testNotPassed += 1;
        } else if (!specialCharRegex.test(password)) {
          testNotPassed += 1;
        } else {
          const consecutiveCharsRegex = /(\w)\1{1,}/;
          if (consecutiveCharsRegex.test(password)) {
            errorMessage =
              "Password must be at least 14 characters and at least 3 of the following character types: One uppercase letter, lowercase letter, number, non-alphanumeric character. Password must not contain any consecutive or identical characters.";
          }
        }
        if (testNotPassed > 1) {
          errorMessage =
            "Password must be at least 14 characters and at least 3 of the following character types: One uppercase letter, lowercase letter, number, non-alphanumeric character. Password must not contain any consecutive or identical characters.";
        }
      }
    }

    return errorMessage;
  };

  const handleEmailChange = (event) => {
    setEmail(event.target.value);
  };

  const handleRequestResetPassword = async () => {
    try {
      const result = await requestResetPassword(email);
      if (result.data.requestId) {
        setShowResetSuccess(true);
      }
    } catch {
      setError("Invalid email address or inactive user");
    }
  };

  const handlePasswordChange = (event) => {
    const newPassword = event.target.value;
    const passwordError = validatePassword(newPassword);
    setNewPassword(newPassword);
    if (passwordError) {
      setError(passwordError);
    } else if (confirmNewPassword !== newPassword) {
      setError("Passwords do not match.");
    } else {
      setError("");
    }
  };

  const handleConfirmPasswordChange = (event) => {
    const confirmNewPassword = event.target.value;
    const passwordError = validatePassword(newPassword);
    setConfirmNewPassword(confirmNewPassword);
    if (passwordError) {
      setError(passwordError);
    } else if (confirmNewPassword !== newPassword) {
      setError("Passwords do not match.");
    } else {
      setError("");
    }
  };

  const isUpdateButtonDisabled = () => {
    const passwordError = validatePassword(newPassword);
    const passwordsMatch = newPassword === confirmNewPassword;

    return !!passwordError || !passwordsMatch;
  };

  const handleResetPassword = async () => {
    try {
      const result = await resetPassword(requestId, newPassword);
      if (result.data) {
        setShowResetSuccess(true);
      }
    } catch (error) {
      if (error?.status === 400) {
        setError("New password cannot be same as old password.");
      } else {
        setError("Invalid request or link has expired");
      }
    }
  };

  const containerStyle = {
    display: "flex",
    width: "100%",
    height: "100%",
    alignItems: "center",
    justifyContent: "space-between",
    px: 16,
    backgroundImage: "url('assets/background/loginbg.png'), url('assets/background/loginbg2.png')",
    backgroundSize: "auto 100%, auto 100%",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "left, right",
  };

  const containerMobileStyle = {
    display: "flex",
    width: "100%",
    height: "100%",
    alignItems: "center",
    justifyContent: "space-between",
  };

  const rightBoxStyle = {
    width: 500,
    borderRadius: 5,
    display: "flex",
    flexDirection: "column",
    p: 2,
    textAlign: "center",
    backgroundColor: theme.palette.background.default,
  };

  const mobileBoxStyle = {
    height: "100%",
    width: "100%",
    borderRadius: 5,
    display: "flex",
    flexDirection: "column",
    p: 2,
    textAlign: "center",
    backgroundColor: theme.palette.background.default,
  };

  const typographyStyle = {
    width: "120px",
    height: "28px",
    lineHeight: "28px",
    mt: 2,
    textAlign: "center",
    backgroundColor: "#5ABBF3",
    position: "absolute",
    right: 0,
  };

  const returnButtonStyle = {
    background: "#9BD3DD",
    borderRadius: "30px",
    minWidth: "155px",
    py: 0.5,
    pr: 1.5,
    pl: 1,
    m: "0 auto 20px",
    "&:hover": { backgroundColor: "#9BD3DD" },
  };

  const renderLoginForm = () => (
    <Box sx={isBrowser ? rightBoxStyle : mobileBoxStyle}>
      <MobileView>
        <img height={40} alt="logo" src="assets/illustrations/clp.png" />
        <Typography variant="subtitle1" color="#717273" fontWeight={700} fontSize={14} mb={3}>
          Common Data Environment
        </Typography>
      </MobileView>
      <Box display="flex" flexDirection="column">
        <MobileView>
          <Box width="100%" textAlign="left" px={1}>
            <Typography variant="subtitle1" color="#333" fontWeight={700}>
              Email Address
            </Typography>
          </Box>
        </MobileView>
        <TextField
          placeholder="Email"
          value={username}
          onChange={(event) => setUsername(event.target.value)}
          sx={{
            m: 1,
            "& .MuiInputBase-root": {
              borderRadius: "10px",
            },
          }}
        />
        <MobileView>
          <Box width="100%" textAlign="left" px={1}>
            <Typography variant="subtitle1" color="#333" fontWeight={700}>
              Password
            </Typography>
          </Box>
        </MobileView>
        <PasswordField
          placeholder="Password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          sx={{
            m: 1,
            "& .MuiInputBase-root": {
              borderRadius: "10px",
            },
          }}
        ></PasswordField>
        {error && (
          <Typography variant="subtitle1" sx={{ color: "#FF0000" }}>
            {error}
          </Typography>
        )}
        <Button variant="contained" color="success" onClick={login} sx={{ m: 1, borderRadius: "10px", height: 51 }}>
          <Typography fontSize={20}>Log in</Typography>
        </Button>
        <BrowserView>
          <img src="assets/illustrations/rectangleline.svg" alt="rectangleline" style={{ margin: "auto" }} />
        </BrowserView>
        <Button variant="contained" onClick={handleSingleSignOn} sx={{ m: 1, borderRadius: "10px", height: 51 }}>
          <Typography fontSize={20}>{isBrowser ? "Single Sign-On" : "SSO"}</Typography>
        </Button>
        <MobileView>
          <Button
            variant="contained"
            onClick={handleClickForgotPassword}
            sx={{ m: 1, borderRadius: "10px", height: 51, backgroundColor: "#B9D9EB", width: "calc(100% - 16px)" }}
          >
            <Typography fontSize={20}>Forgot Password</Typography>
          </Button>
        </MobileView>
      </Box>
      <Box display="flex" flexDirection="column">
        <Divider sx={{ m: 1 }}></Divider>
        <Typography variant="p" sx={{ m: 1, fontSize: 16 }}>
          Please contact project management for new account creation.{" "}
          <BrowserView>
            <span style={{ color: "blue", textDecoration: "underline", cursor: "pointer" }} onClick={handleClickForgotPassword}>
              Click Here
            </span>{" "}
            for forgotten password.
          </BrowserView>
        </Typography>
        <MobileView>
          <Box display="flex" gap={1} justifyContent="center" my={2}>
            <Typography fontSize={10}>powered by</Typography>{" "}
            <img src="assets/background/headerbg.png" alt="header" style={{ width: 15, height: 15 }}></img>
          </Box>
        </MobileView>
      </Box>
    </Box>
  );

  const renderRedirectingForm = () => (
    <Box sx={isBrowser ? rightBoxStyle : mobileBoxStyle}>
      <MobileView>
        <img height={40} alt="logo" src="assets/illustrations/clp.png" />
        <Typography variant="subtitle1" color="#717273" fontWeight={700} fontSize={14} mb={3}>
          Common Data Environment
        </Typography>
      </MobileView>
      <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
        <CircularProgress></CircularProgress>
        <Typography variant="subtitle1" color="#717273" fontWeight={700} fontSize={14}>
          {ssoStatus}
        </Typography>
      </Box>
    </Box>
  );

  const renderRequestResetPasswordForm = () => (
    <Box sx={isBrowser ? rightBoxStyle : mobileBoxStyle}>
      <MobileView>
        <img height={40} alt="logo" src="assets/illustrations/clp.png" />
        <Typography variant="subtitle1" color="#717273" fontWeight={700} fontSize={14} mb={3}>
          Common Data Environment
        </Typography>
      </MobileView>
      <Typography variant="h4" sx={{ fontWeight: 700, color: "#717273" }}>
        Reset Password
      </Typography>
      <Divider sx={{ m: 1 }}></Divider>
      {showResetSuccess ? (
        <>
          <CheckCircleOutlineOutlined sx={{ fontSize: "3.6rem", color: "#00B176", m: "16px auto" }} />
          <Typography variant="h5" sx={{ my: 1, fontWeight: 600, color: "#717273" }}>
            Email sent
          </Typography>
          <Typography sx={{ mt: 1.5, mb: 4 }}>An email have been sent to {email} to reset your password </Typography>
        </>
      ) : (
        <>
          <MobileView>
            <Box width="100%" textAlign="left" px={1}>
              <Typography variant="subtitle1" color="#333" fontWeight={700}>
                Email Address
              </Typography>
            </Box>
          </MobileView>
          <TextField
            placeholder="Email"
            value={email}
            onChange={handleEmailChange}
            sx={{
              m: 1,
              "& .MuiInputBase-root": {
                borderRadius: "10px",
              },
            }}
            type="email"
          />
          {error && (
            <Typography variant="subtitle1" sx={{ color: "#FF0000" }}>
              {error}
            </Typography>
          )}
          <Button variant="contained" color="success" onClick={handleRequestResetPassword} sx={{ m: 1, borderRadius: "10px", height: 51 }}>
            Submit
          </Button>
        </>
      )}
      <Button
        variant="contained"
        sx={returnButtonStyle}
        startIcon={<img src="assets/buttons/back.svg" alt="back" />}
        onClick={() => {
          setShowResetPassword(false);
          setShowResetSuccess(false);
          setError("");
        }}
      >
        Return to Login
      </Button>
      <Divider sx={{ m: 1 }}></Divider>
      <Typography variant="p" sx={{ my: 1 }}>
        Please enter your associated email to get instructions to reset your password.
      </Typography>
      <MobileView>
        <Box display="flex" gap={1} justifyContent="center" my={2}>
          <Typography fontSize={10}>powered by</Typography>{" "}
          <img src="assets/background/headerbg.png" alt="header" style={{ width: 15, height: 15 }}></img>
        </Box>
      </MobileView>
    </Box>
  );

  const renderResetPasswordForm = () => (
    <Box sx={isBrowser ? rightBoxStyle : mobileBoxStyle}>
      <MobileView>
        <img height={40} alt="logo" src="assets/illustrations/clp.png" />
        <Typography variant="subtitle1" color="#717273" fontWeight={700} fontSize={14} mb={3}>
          Common Data Environment
        </Typography>
      </MobileView>
      <Typography variant="h4" sx={{ fontWeight: 700, color: "#717273" }}>
        {reset ? "Reset Password" : "Setup Password"}
      </Typography>
      <Divider sx={{ m: 1 }}></Divider>
      {showResetSuccess ? (
        <>
          <CheckCircleOutlineOutlined sx={{ fontSize: "3.6rem", color: "#00B176", m: "16px auto" }} />
          <Typography variant="h5" sx={{ my: 1, fontWeight: 600, color: "#717273" }}>
            {reset ? "Password Reset" : "Password setup"}
          </Typography>
          <Typography sx={{ mt: 1.5, mb: 4 }}>Please return to login page</Typography>
          <Button
            variant="contained"
            sx={returnButtonStyle}
            startIcon={<img src="assets/buttons/back.svg" alt="back" />}
            onClick={() => {
              setShowResetPassword(false);
              setShowResetSuccess(false);
              setNewPassword("");
              setConfirmNewPassword("");
              searchParams.delete("requestId");
              searchParams.delete("privileged");
              setSearchParams(searchParams);
            }}
          >
            Return to Login
          </Button>
          <Divider sx={{ m: 1 }}></Divider>
          <Typography variant="p" sx={{ my: 1 }}>
            Please return and login.
          </Typography>
        </>
      ) : isValidRequest ? (
        <>
          <MobileView>
            <Box width="100%" textAlign="left" px={1}>
              <Typography variant="subtitle1" color="#333" fontWeight={700}>
                New Password
              </Typography>
            </Box>
          </MobileView>
          <PasswordField
            placeholder="Please Enter New Password"
            value={newPassword}
            onChange={handlePasswordChange}
            sx={{
              m: 1,
              "& .MuiInputBase-root": {
                borderRadius: "10px",
              },
            }}
          ></PasswordField>
          <MobileView>
            <Box width="100%" textAlign="left" px={1}>
              <Typography variant="subtitle1" color="#333" fontWeight={700}>
                Confirm Password
              </Typography>
            </Box>
          </MobileView>
          <PasswordField
            placeholder="Repeat Your Password"
            value={confirmNewPassword}
            onChange={handleConfirmPasswordChange}
            sx={{
              m: 1,
              "& .MuiInputBase-root": {
                borderRadius: "10px",
              },
            }}
          ></PasswordField>
          {error && (
            <Typography variant="subtitle1" sx={{ color: "#FF0000" }}>
              {error}
            </Typography>
          )}
          <Button
            variant="contained"
            color="success"
            onClick={handleResetPassword}
            disabled={isUpdateButtonDisabled()}
            sx={{ m: 1, borderRadius: "10px", height: 51, "&.Mui-disabled": { opacity: "0.5!important" } }}
          >
            Update
          </Button>
          <Divider sx={{ m: 1 }}></Divider>
          <Typography variant="p" sx={{ my: 1 }}>
            Please input your new password.
          </Typography>
        </>
      ) : (
        <>
          <Typography variant={"h6"} sx={{ my: 5 }}>
            Invalid request or request expired. Please request again.
          </Typography>
          <Button
            variant="contained"
            sx={returnButtonStyle}
            startIcon={<img src="assets/buttons/back.svg" alt="back" />}
            onClick={() => {
              setShowResetPassword(false);
              setShowResetSuccess(false);
              setNewPassword("");
              setConfirmNewPassword("");
              searchParams.delete("requestId");
              searchParams.delete("privileged");
              setSearchParams(searchParams);
            }}
          >
            Return to Login
          </Button>
        </>
      )}
      <MobileView>
        <Box display="flex" gap={1} justifyContent="center" my={2}>
          <Typography fontSize={10}>powered by</Typography>{" "}
          <img src="assets/background/headerbg.png" alt="header" style={{ width: 15, height: 15 }}></img>
        </Box>
      </MobileView>
    </Box>
  );

  const renderSSOFailure = () => (
    <Box sx={rightBoxStyle}>
      <Typography variant="h4" sx={{ fontWeight: 700, color: "#717273" }}>
        Single Sign-On
      </Typography>
      <Divider sx={{ m: 1 }}></Divider>
      <HighlightOff sx={{ fontSize: "3.6rem", color: "#FF0000", m: "16px auto" }} />
      <Typography variant="h5" sx={{ my: 1, fontWeight: 600, color: "#717273" }}>
        Authentication failed
      </Typography>
      <Typography sx={{ mt: 1.5, mb: 3, mx: 1 }}>
        Single Sign-On authentication failed. Please contact your site administrator to request access or log in with another method
      </Typography>
      <Button
        variant="contained"
        sx={returnButtonStyle}
        startIcon={<img src="assets/buttons/back.svg" alt="back" />}
        onClick={() => setSsoStatus(null)}
      >
        Return to Login
      </Button>
      <Divider sx={{ m: 1 }}></Divider>
      <Typography variant="p" sx={{ m: 1 }}>
        Please contact project management for new account creation.{" "}
        <span style={{ color: "blue", textDecoration: "underline", cursor: "pointer" }} onClick={handleClickForgotPassword}>
          Click Here
        </span>{" "}
        for forgotten password.
      </Typography>
    </Box>
  );

  const renderAccountLocked = () => {
    const now = dayjs();
    const timeLeft = accountLockedUntil ? accountLockedUntil.valueOf() - now.valueOf() : 0;
    const minutesLeft = Math.floor(timeLeft / (1000 * 60));
    const secondsLeft = Math.floor((timeLeft % (1000 * 60)) / 1000);

    return (
      <Box sx={{ ...rightBoxStyle, mt: 2 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: "#717273" }}>
          Account Locked
        </Typography>
        <Divider sx={{ m: 1 }}></Divider>
        <HighlightOff sx={{ fontSize: "3.6rem", color: "#FF0000", m: "16px auto" }} />
        <Typography variant="h5" sx={{ my: 1, fontWeight: 600, color: "#717273" }}>
          Too many failed login attempts
        </Typography>
        <Typography sx={{ mt: 1.5, mb: 3, mx: 1 }}>
          Your account has been temporarily locked. Please try again in {minutesLeft} minutes and {secondsLeft} seconds.
        </Typography>
        <Button
          variant="contained"
          sx={{ ...returnButtonStyle, minWidth: "100px" }}
          startIcon={<img src="assets/buttons/back.svg" alt="back" />}
          onClick={() => {
            setAccountLockedUntil(null);
            setShowResetPassword(false);
            setError("");
          }}
        >
          Back
        </Button>
        <Divider sx={{ m: 1 }}></Divider>
        <Typography variant="p" sx={{ m: 1 }}>
          Please contact project management for new account creation.{" "}
          <span style={{ color: "blue", textDecoration: "underline", cursor: "pointer" }} onClick={handleClickForgotPassword}>
            Click Here
          </span>{" "}
          for forgotten or expired password.
        </Typography>
      </Box>
    );
  };

  return requestId ? (
    <Box
      sx={
        isBrowser
          ? {
              ...containerStyle,
              justifyContent: "flex-end",
              gap: 20,
              backgroundImage: "url('assets/background/loginbg.png'), url('assets/background/loginbg3.png')",
              backgroundSize: "auto 100%, auto 50%",
              backgroundColor: "#B9D9EB",
            }
          : containerMobileStyle
      }
    >
      {renderResetPasswordForm()}
      <Box
        sx={{
          textAlign: "left",
          color: "black",
          position: "relative",
          paddingRight: 4,
        }}
      >
        <Typography variant="h3" sx={{ fontWeight: 500 }}>
          CLP
          <br />
          Project
          <br />
          Workplace
        </Typography>
        {/* <Typography variant="h4">CLP Construction</Typography>
        <Typography variant="body1" sx={{ ...typographyStyle, color: "#fff", left: 0 }}>
          powered by CLP
        </Typography> */}
      </Box>
    </Box>
  ) : (
    <Box sx={isBrowser ? containerStyle : containerMobileStyle}>
      <BrowserView>
        <Box
          sx={{
            textAlign: "right",
            color: "#fff",
            position: "relative",
          }}
        >
          <Typography variant="h3" sx={{ fontWeight: 500 }}>
            CLP
            <br />
            Project
            <br />
            Workplace
          </Typography>
          {/* <Typography variant="h4">CLP Construction</Typography>
          <Typography variant="body1" sx={typographyStyle}>
            powered by CLP
          </Typography> */}
        </Box>
      </BrowserView>
      {accountLockedUntil ? renderAccountLocked() : null}
      {!showResetPassword && !ssoStatus && !accountLockedUntil ? renderLoginForm() : null}
      {showResetPassword && !ssoStatus && !accountLockedUntil ? renderRequestResetPasswordForm() : null}
      {ssoStatus === "failed" && !accountLockedUntil ? renderSSOFailure() : null}
      {ssoStatus && ssoStatus !== "failed" ? renderRedirectingForm() : null}
    </Box>
  );
};

export default LoginPage;
